import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CheckIcon from '@mui/icons-material/Check';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';

import { useTheme } from 'contexts/ThemeContext';

import { Text } from 'components/DataDisplay';
import {
  Button,
  IconButton,
  MultipleSelectSearch,
  Select
} from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { Paper } from 'components/Surfaces';

const normalizeOption = option => {
  return {
    value: option?.value ?? option?.key ?? '',
    label: option?.label ?? option?.value ?? option?.key ?? ''
  };
};

const getInitialDraft = (firstFieldKey, secondFieldKey) => {
  return {
    [firstFieldKey]: [],
    [secondFieldKey]: ''
  };
};

const getValuesAsArray = value => {
  if (Array.isArray(value)) {
    return value;
  }

  if (value) {
    return [value];
  }

  return [];
};

const getUniqueValues = value => {
  return [...new Set(getValuesAsArray(value).filter(Boolean))];
};

const getRowKey = (row, firstFieldKey, secondFieldKey) => {
  const firstFieldValues = [...getUniqueValues(row?.[firstFieldKey])].sort();

  return `${firstFieldValues.join('-')}-${row?.[secondFieldKey] || ''}`;
};

const groupRowsBySecondField = (rows, firstFieldKey, secondFieldKey) => {
  const grouped = rows.reduce((acc, row) => {
    const secondFieldValue = row?.[secondFieldKey];
    const firstFieldValues = getUniqueValues(row?.[firstFieldKey]);

    if (!secondFieldValue || firstFieldValues.length === 0) {
      return acc;
    }

    if (!acc[secondFieldValue]) {
      acc[secondFieldValue] = new Set();
    }

    firstFieldValues.forEach(value => {
      acc[secondFieldValue].add(value);
    });

    return acc;
  }, {});

  return Object.keys(grouped).map(secondFieldValue => ({
    [firstFieldKey]: [...grouped[secondFieldValue]],
    [secondFieldKey]: secondFieldValue
  }));
};

export const DoubleConditionalListField = props => {
  const {
    values,
    onChange,
    actionTxt,
    firstField,
    secondField,
    placeholderTxt
  } = props;
  const { themeColors } = useTheme();

  const firstFieldKey = firstField.key;
  const secondFieldKey = secondField.key;

  const [editingRowKey, setEditingRowKey] = useState(null);
  const [editingMediaValue, setEditingMediaValue] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [draft, setDraft] = useState(
    getInitialDraft(firstFieldKey, secondFieldKey)
  );

  const normalizedFirstOptions = useMemo(() => {
    return (firstField.options || []).map(normalizeOption);
  }, [firstField.options]);

  const optionLabelByValue = useMemo(() => {
    return normalizedFirstOptions.reduce((acc, option) => {
      acc[option.value] = option.label;
      return acc;
    }, {});
  }, [normalizedFirstOptions]);

  const draftFirstValues = useMemo(() => {
    return getUniqueValues(draft[firstFieldKey]);
  }, [draft, firstFieldKey]);

  const getSecondOptions = useCallback(
    firstValues => {
      if (typeof secondField.getOptions !== 'function') {
        return [];
      }

      return secondField.getOptions(getValuesAsArray(firstValues)) || [];
    },
    [secondField]
  );

  const usedFirstValues = useMemo(() => {
    return values.reduce((acc, row) => {
      const rowKey = getRowKey(row, firstFieldKey, secondFieldKey);
      const rowFirstValues = getUniqueValues(row?.[firstFieldKey]);

      if (editingRowKey && rowKey === editingRowKey) {
        return [
          ...acc,
          ...rowFirstValues.filter(value => value !== editingMediaValue)
        ];
      }

      return [...acc, ...rowFirstValues];
    }, []);
  }, [editingMediaValue, editingRowKey, firstFieldKey, secondFieldKey, values]);

  const availableFirstOptions = useMemo(() => {
    return normalizedFirstOptions.filter(option => {
      return (
        draftFirstValues.includes(option.value) ||
        !usedFirstValues.includes(option.value)
      );
    });
  }, [draftFirstValues, normalizedFirstOptions, usedFirstValues]);

  const selectedFirstOptions = useMemo(() => {
    return availableFirstOptions.filter(option => {
      return draftFirstValues.includes(option.value);
    });
  }, [availableFirstOptions, draftFirstValues]);

  const secondOptions = useMemo(() => {
    return getSecondOptions(draftFirstValues);
  }, [draftFirstValues, getSecondOptions]);

  const canAddRow = useMemo(() => {
    return availableFirstOptions.length > 0;
  }, [availableFirstOptions]);

  const isDraftValid = useMemo(() => {
    return draftFirstValues.length > 0 && !!draft[secondFieldKey];
  }, [draft, draftFirstValues, secondFieldKey]);

  const displayRows = useMemo(() => {
    return values.reduce((acc, row) => {
      const medias = getUniqueValues(row?.[firstFieldKey]);

      medias.forEach(media => {
        acc.push({
          media,
          row,
          rowKey: getRowKey(row, firstFieldKey, secondFieldKey)
        });
      });

      return acc;
    }, []);
  }, [firstFieldKey, secondFieldKey, values]);

  const resetDraft = useCallback(() => {
    setEditingRowKey(null);
    setEditingMediaValue(null);
    setIsFormVisible(false);
    setDraft(getInitialDraft(firstFieldKey, secondFieldKey));
  }, [firstFieldKey, secondFieldKey]);

  const handleShowForm = () => {
    setEditingRowKey(null);
    setEditingMediaValue(null);
    setIsFormVisible(true);
    setDraft(getInitialDraft(firstFieldKey, secondFieldKey));
  };

  const handleChangeFirstField = selectedOptions => {
    const nextFirstValues = (selectedOptions || []).map(option => {
      return option.value ?? option.key ?? option;
    });

    const nextSecondOptions = getSecondOptions(nextFirstValues);
    const currentSecondStillValid = nextSecondOptions.some(option => {
      return option.value === draft[secondFieldKey];
    });

    setDraft(prevState => ({
      ...prevState,
      [firstFieldKey]: nextFirstValues,
      [secondFieldKey]: currentSecondStillValid ? prevState[secondFieldKey] : ''
    }));
  };

  const handleChangeSecondField = value => {
    setDraft(prevState => ({
      ...prevState,
      [secondFieldKey]: value
    }));
  };

  const handleEdit = (row, media) => {
    setEditingRowKey(getRowKey(row, firstFieldKey, secondFieldKey));
    setEditingMediaValue(media);
    setIsFormVisible(true);
    setDraft({
      [firstFieldKey]: [media],
      [secondFieldKey]: row?.[secondFieldKey] || ''
    });
  };

  const onDelete = (row, media) => {
    let copyValues = [...values];
    const index = copyValues.findIndex(
      el =>
        el[firstFieldKey] === row[firstFieldKey] &&
        el[secondFieldKey] === row[secondFieldKey]
    );

    if (copyValues[index][firstFieldKey].length === 1) {
      copyValues = copyValues.filter(
        el =>
          el[firstFieldKey] !== row[firstFieldKey] &&
          el[secondFieldKey] !== row[secondFieldKey]
      );
    } else {
      copyValues[index][firstFieldKey] = copyValues[index][
        firstFieldKey
      ].filter(el => el !== media);
    }

    onChange(copyValues);
  };

  const handleValidate = () => {
    if (!isDraftValid) {
      return;
    }

    const nextRow = {
      [firstFieldKey]: draftFirstValues,
      [secondFieldKey]: draft[secondFieldKey]
    };

    if (editingRowKey === null) {
      onChange(
        groupRowsBySecondField(
          [...(values || []), nextRow],
          firstFieldKey,
          secondFieldKey
        )
      );
      resetDraft();
      return;
    }

    const nextValues = [];

    (values || []).forEach(row => {
      const rowKey = getRowKey(row, firstFieldKey, secondFieldKey);

      if (rowKey !== editingRowKey) {
        nextValues.push(row);
        return;
      }

      const remainingFirstValues = getUniqueValues(row?.[firstFieldKey]).filter(
        value => !draftFirstValues.includes(value)
      );

      if (remainingFirstValues.length > 0) {
        nextValues.push({
          ...row,
          [firstFieldKey]: remainingFirstValues
        });
      }
    });

    nextValues.push(nextRow);

    onChange(groupRowsBySecondField(nextValues, firstFieldKey, secondFieldKey));
    resetDraft();
  };

  const getFirstFieldDisplay = mediaValue => {
    return optionLabelByValue[mediaValue] || mediaValue;
  };

  const getSecondFieldDisplay = row => {
    const rowSecondOptions = getSecondOptions(row?.[firstFieldKey]);
    const foundOption = rowSecondOptions.find(option => {
      return option.value === row?.[secondFieldKey];
    });

    return foundOption?.label || row?.[secondFieldKey] || '';
  };

  useEffect(() => {
    if (!editingRowKey) {
      return;
    }

    const hasEditingRow = (values || []).some(row => {
      return getRowKey(row, firstFieldKey, secondFieldKey) === editingRowKey;
    });

    if (!hasEditingRow) {
      resetDraft();
    }
  }, [editingRowKey, firstFieldKey, secondFieldKey, resetDraft, values]);

  return (
    <Container>
      {!!displayRows.length && (
        <Row spacing={0} justify="flex-start" style={{ marginBottom: '8px' }}>
          <Item xs={5} justify="flex-start">
            <Text fontWeight={700}>{firstField.label}</Text>
          </Item>
          <Item xs={5} justify="flex-start">
            <Text fontWeight={700}>{secondField.label}</Text>
          </Item>
          <Item xs={1} />
        </Row>
      )}
      {displayRows.map(({ media, row, rowKey }) => {
        return (
          <Row spacing={2} justify="flex-start">
            <Item>
              <Paper style={{ flex: 1 }}>
                <Container>
                  <Row key={`${rowKey}-${media}`} spacing={0}>
                    <Item
                      xs={5}
                      justify="flex-start"
                      style={{
                        padding: '5px'
                      }}
                    >
                      <Text>{getFirstFieldDisplay(media)}</Text>
                    </Item>
                    <Item
                      xs={5}
                      justify="flex-start"
                      style={{
                        padding: '5px'
                      }}
                    >
                      <Text>{getSecondFieldDisplay(row)}</Text>
                    </Item>
                    <Item xs justify="center">
                      <IconButton onClick={() => handleEdit(row, media)}>
                        <CreateIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          onDelete(row, media);
                        }}
                      >
                        <DeleteIcon
                          style={{
                            color: themeColors.light
                          }}
                        />
                      </IconButton>
                    </Item>
                  </Row>
                </Container>
              </Paper>
            </Item>
          </Row>
        );
      })}
      {isFormVisible ? (
        <Row spacing={2} justify="flex-start" alignItems="flex-end">
          <Item xs={5} justify="flex-start" style={{ paddingRight: '12px' }}>
            <MultipleSelectSearch
              small
              title={firstField.label}
              placeholder={placeholderTxt}
              values={selectedFirstOptions}
              options={availableFirstOptions}
              onChange={handleChangeFirstField}
              selectAll
            />
          </Item>

          <Item xs={5} justify="flex-start" style={{ paddingRight: '12px' }}>
            <Select
              small
              title={secondField.label}
              value={draft[secondFieldKey]}
              options={secondOptions}
              onChange={handleChangeSecondField}
              disabled={draftFirstValues.length === 0}
            />
          </Item>

          <Item xs={1} justify="center" style={{ paddingBottom: '6px' }}>
            <IconButton disabled={!isDraftValid} onClick={handleValidate}>
              <CheckIcon />
            </IconButton>
          </Item>
        </Row>
      ) : (
        canAddRow && (
          <Row spacing={2} justify="flex-start">
            <Item justify="flex-start">
              <Button variant="contained" onClick={handleShowForm}>
                {actionTxt}
              </Button>
            </Item>
          </Row>
        )
      )}
    </Container>
  );
};

DoubleConditionalListField.defaultProps = {
  values: [],
  onChange: () => null,
  actionTxt: 'Ajouter une ligne',
  placeholderTxt: 'Ajouter un élement'
};

DoubleConditionalListField.propTypes = {
  values: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  onChange: PropTypes.func,
  actionTxt: PropTypes.string,
  placeholderTxt: PropTypes.string,
  firstField: PropTypes.shape({
    label: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any))
  }).isRequired,
  secondField: PropTypes.shape({
    label: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    getOptions: PropTypes.func
  }).isRequired
};

export default DoubleConditionalListField;
